import type {
  OrderItem,
  ParticipantInput,
  Simulation,
} from '~types/appsync.types';
import type {
  CartProduct,
  ParticipantDisplay,
  ParticipantOptionDisplay,
  ProductDisplay,
} from '~types/cart';
import type { ParticipantOption } from '~types/participant';

import { convertParticipantToParticipantInput } from './convertFunctions';

export interface FlatParticipant {
  input: ParticipantInput;
  productId: string;
  localParticipantId: number;
}

export const formatTimeslotLabel = (
  date: string,
  duration?: number | null,
): string | undefined => {
  const match = /T(\d{2}):(\d{2})/.exec(date ?? '');
  if (!match) return undefined;
  const startH = Number.parseInt(match[1], 10);
  const startM = Number.parseInt(match[2], 10);
  const pad = (n: number) => n.toString().padStart(2, '0');
  const startLabel = `${pad(startH)}:${pad(startM)}`;
  if (!duration) return startLabel;
  const totalMin = startH * 60 + startM + duration;
  const endH = Math.floor(totalMin / 60) % 24;
  const endM = totalMin % 60;
  return `${startLabel} – ${pad(endH)}:${pad(endM)}`;
};

const productTimeslotLabel = (product: CartProduct): string | undefined => {
  const timeslot = product.timeslot;
  if (!timeslot || timeslot.dailyBooking) return undefined;
  return formatTimeslotLabel(timeslot.date, timeslot.duration);
};

const optionTimeslotLabel = (option: ParticipantOption): string | undefined => {
  const timeslot = option.timeslot;
  if (!timeslot || timeslot.dailyBooking) return undefined;
  return formatTimeslotLabel(timeslot.date, timeslot.duration);
};

const resolveOptionDisplay = (
  slug: string,
  total: number,
  options: ParticipantOption[],
): ParticipantOptionDisplay => {
  const match = options.find((o) => o.slug === slug);
  return {
    label: match?.name ?? slug,
    priceInCents: total,
    timeslotLabel: match ? optionTimeslotLabel(match) : undefined,
  };
};

export const buildFlatParticipants = (products: CartProduct[]): FlatParticipant[] => {
  const flat: FlatParticipant[] = [];
  // Participant IDs are 1-based: the simulation API expects IDs starting at 1.
  let globalId = 1;
  products.forEach((product) => {
    product.participants.forEach((participant) => {
      const input = convertParticipantToParticipantInput(
        participant,
        product.productSlug,
        product.date,
      );
      flat.push({
        input: { ...input, id: globalId },
        localParticipantId: participant.id,
        productId: product.id,
      });
      globalId += 1;
    });
  });
  return flat;
};

const buildPlaceholderDisplay = (product: CartProduct): ProductDisplay => ({
  currency: 'EUR',
  optionCount: product.participants.reduce((sum, p) => sum + p.options.length, 0),
  participants: product.participants.map(
    (participant): ParticipantDisplay => ({
      consumerCategoryLabel: participant.consumerCategory?.name ?? '',
      options: participant.options.map((option) => ({
        label: option.name,
        priceInCents: 0,
        timeslotLabel: optionTimeslotLabel(option),
      })),
      participantId: participant.id,
      priceInCents: 0,
    }),
  ),
  productCategoryName: product.productCategoryName,
  productId: product.id,
  skiDate: product.displayCalendar === false ? undefined : product.date,
  timeslotLabel: productTimeslotLabel(product),
  totalInCents: 0,
  validityCategoryLabel: product.validityCategoryName,
});

const buildResolvedDisplay = (
  product: CartProduct,
  simulation: Simulation,
  orderItemByGlobalId: Map<number, OrderItem>,
  globalIdByParticipantKey: Map<string, number>,
): ProductDisplay => {
  const participants: ParticipantDisplay[] = product.participants.map((participant) => {
    const globalId = globalIdByParticipantKey.get(`${product.id}:${participant.id}`);
    const item = globalId === undefined ? undefined : orderItemByGlobalId.get(globalId);
    return {
      consumerCategoryLabel: participant.consumerCategory?.name ?? '',
      options:
        item?.children.map((child) =>
          resolveOptionDisplay(child.slug, child.total, participant.options),
        ) ?? [],
      participantId: participant.id,
      priceInCents: item?.total ?? 0,
    };
  });

  const totalInCents = participants.reduce(
    (sum, p) => sum + p.priceInCents + p.options.reduce((s, o) => s + o.priceInCents, 0),
    0,
  );
  const optionCount = participants.reduce((sum, p) => sum + p.options.length, 0);

  return {
    currency: simulation.currency,
    optionCount,
    participants,
    productCategoryName: product.productCategoryName,
    productId: product.id,
    skiDate: product.displayCalendar === false ? undefined : product.date,
    timeslotLabel: productTimeslotLabel(product),
    totalInCents,
    validityCategoryLabel: product.validityCategoryName,
  };
};

export const buildProductDisplays = (
  products: CartProduct[],
  flatParticipants: FlatParticipant[],
  simulation: Simulation | undefined,
): ProductDisplay[] => {
  if (!simulation) {
    return products.map(buildPlaceholderDisplay);
  }

  const orderItemByGlobalId = new Map<number, OrderItem>();
  simulation.orderItems.forEach((item) => {
    orderItemByGlobalId.set(item.participantId, item);
  });

  const globalIdByParticipantKey = new Map<string, number>();
  flatParticipants.forEach((flatParticipant) => {
    globalIdByParticipantKey.set(
      `${flatParticipant.productId}:${flatParticipant.localParticipantId}`,
      flatParticipant.input.id,
    );
  });

  return products.map((product) =>
    buildResolvedDisplay(product, simulation, orderItemByGlobalId, globalIdByParticipantKey),
  );
};
